import { createFileRoute, Link } from "@tanstack/react-router";
import { getHomeMakes, type HomeMake } from "@/lib/home-makes.functions";
import { getPopularComparisons } from "@/lib/compare.functions";
import type { ComparePairEntry } from "@/lib/compare.server";
import { itemListLd, faqLd, SITE_OG_IMAGE } from "@/lib/seo";
import { HomeSearchBox } from "@/components/HomeSearchBox";
import { TOPICS } from "@/lib/topics-config";
import { BrandLogo } from "@/components/BrandLogo";
import { AdSlot } from "@/components/AdSlot";
import { Fuel, Cog, Ruler, Caravan, ShieldCheck, PiggyBank, ArrowRight, type LucideIcon } from "lucide-react";

const TITLE = "Vehicle Specs, MPG & Towing Capacity | AutoSpecData";
const DESCRIPTION =
  "Free verified specs for 15,800+ cars, trucks and SUVs (2015–2027): MPG, engine, towing capacity, dimensions, tire sizes and safety ratings.";

/**
 * High-intent search phrases, chosen from Semrush US volume/difficulty data —
 * each has real monthly demand and a difficulty a young domain can win:
 * tacoma towing capacity (9.9k/KD16), truck bed dimensions (1.6k/KD17),
 * towing capacity by vin (2.4k/KD25), fuel tank capacity (880/KD9),
 * car size comparison (9.9k), car maintenance schedule (6.6k).
 */
const POPULAR_SEARCHES: { label: string; q: string }[] = [
  { label: "Toyota Tacoma towing capacity", q: "toyota tacoma" },
  { label: "Ford F-150 towing capacity", q: "ford f-150" },
  { label: "Truck bed dimensions", q: "silverado" },
  { label: "Jeep Wrangler fuel tank capacity", q: "jeep wrangler" },
  { label: "Ram 1500 payload capacity", q: "ram 1500" },
  { label: "Tesla Model Y range", q: "tesla model y" },
];

const FAQS = [
  {
    q: "Where does AutoSpecData get its vehicle specifications?",
    a: "Every spec comes from official U.S. government datasets: fuel economy, engine and emissions data from the EPA's fueleconomy.gov database, and VIN decoding, dimensions and crash-test ratings from NHTSA vPIC and NCAP. Nothing is user-submitted or estimated.",
  },
  {
    q: "How do I find the towing capacity for my vehicle?",
    a: "Enter your VIN in the free VIN decoder, or search your make and model and open its specifications page. Towing capacity, payload and GVWR are listed by model year and trim where the manufacturer publishes them.",
  },
  {
    q: "Is AutoSpecData free to use?",
    a: "Yes. Every specification page, comparison, ranking and calculator is free with no sign-up. The site is supported by advertising.",
  },
  {
    q: "How many vehicles are in the database?",
    a: "More than 15,800 vehicle configurations across 62 makes and 619 models, covering model years 2015 through 2027, with new model years added as the EPA publishes them.",
  },
  {
    q: "How often is the data updated?",
    a: "Vehicle data is re-ingested from the EPA and NHTSA feeds on a recurring schedule, so new model years and newly published crash-test ratings appear automatically.",
  },
];

export const Route = createFileRoute("/")({
  // The hero renders from CSS only. A remote Pexels photo cost an extra
  // origin, third-party cookies and ~100 KiB on the LCP path for no content value.
  loader: async () => {
    const [home, comparisons] = await Promise.all([getHomeMakes(), getPopularComparisons()]);
    // getPopularComparisons is already filtered through src/lib/index-policy.ts —
    // only same-segment mainstream rivals, so nothing here links to a noindex page.
    return { ...home, comparisons: comparisons.pairs.slice(0, 6) };
  },
  component: Index,
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://autospecdata.com/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://autospecdata.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://autospecdata.com/#organization",
              name: "AutoSpecData",
              url: "https://autospecdata.com/",
              logo: "https://autospecdata.com/icon-512.png",
              publishingPrinciples: "https://autospecdata.com/methodology",
            },
          ],
        }),
      },
      // WebSite entity is emitted once in __root.tsx — duplicating it here
      // creates two competing WebSite nodes on the homepage.
      { type: "application/ld+json", children: JSON.stringify(faqLd(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          itemListLd(
            "Vehicle makes",
            (loaderData?.makes ?? []).map((m: HomeMake) => ({
              name: m.name,
              path: `/${m.slug}`,
            })),
          ),
        ),
      },
    ],
  }),
});

function StatBlock({ value, label }: { value: string; label: string }) {
  return (
    <div className="px-2">
      <div className="stat-number text-2xl text-surface-foreground sm:text-3xl">{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-surface-foreground/60">{label}</div>
    </div>
  );
}

function SectionHead({ eyebrow, title, intro }: { eyebrow: string; title: string; intro?: string }) {
  return (
    <div className="mb-8 max-w-2xl">
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="mt-2 text-2xl font-bold text-foreground sm:text-3xl">{title}</h2>
      {intro ? <p className="mt-3 text-muted-foreground">{intro}</p> : null}
    </div>
  );
}

/** Small always-visible link cue — hover states don't exist on touch. */
function CardCta({ label }: { label: string }) {
  return (
    <span className="card-cta mt-4">
      {label}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

/**
 * Data-coverage entries. Each one links to the hub that actually serves that
 * spec type, with a relevant oversized icon watermark as the card background.
 */
type CoverageCard = {
  title: string;
  body: string;
  Icon: LucideIcon;
  cta: string;
  href: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
};

function Index() {
  const { makes, comparisons } = Route.useLoaderData();

  return (
    <div>
      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden ink-panel">
        <div className="absolute inset-0 grid-lines opacity-[0.07]" aria-hidden="true" />
        <div
          className="absolute -right-32 -top-40 h-[28rem] w-[28rem] rounded-full opacity-25 blur-3xl"
          style={{ background: "var(--gradient-signal)" }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-surface-foreground/20 bg-surface-foreground/10 px-3.5 py-1.5 text-xs font-semibold text-surface-foreground">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-highlight">
              <path
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            EPA &amp; NHTSA verified · Updated for 2027 models
          </span>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.05] text-surface-foreground text-on-photo sm:text-6xl">
            Vehicle specs, MPG &amp; towing capacity
            <span className="block text-highlight">for every make and model.</span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-surface-foreground/90 text-on-photo sm:text-lg">
            Look up fuel economy, engine specifications, dimensions, towing capacity, tire sizes, safety ratings and
            maintenance intervals — free, with no sign-up, sourced directly from U.S. government data.
          </p>

          <HomeSearchBox />

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs font-medium text-surface-foreground/75">Popular:</span>
            {POPULAR_SEARCHES.map((s) => (
              <Link
                key={s.label}
                to="/search"
                search={{ q: s.q, make: "", model: "", year: undefined, type: "", page: 1 }}
                className="rounded-full border border-surface-foreground/25 bg-surface-foreground/10 px-3 py-1.5 text-xs font-medium text-surface-foreground/95 backdrop-blur-sm transition-colors hover:border-highlight/60 hover:text-highlight"
              >
                {s.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="relative border-t border-surface-foreground/15">
          <div className="mx-auto grid max-w-5xl grid-cols-2 gap-y-6 px-4 py-8 text-center sm:px-6 md:grid-cols-4">
            <StatBlock value="15,800+" label="Vehicles" />
            <StatBlock value="62" label="Makes" />
            <StatBlock value="2015–2027" label="Model years" />
            <StatBlock value="100%" label="Government sourced" />
          </div>
        </div>
      </section>

      {/* ---------- High-intent hubs (bento) ---------- */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <SectionHead
          eyebrow="Start here"
          title="Look up the spec you actually came for"
          intro="Each hub is a full dataset, not a blog post — filterable, cross-linked and covering every model year we hold."
        />

        <div className="grid gap-4 md:grid-cols-6">
          <Link
            to="/vin-decoder"
            className="card-premium group flex flex-col justify-between overflow-hidden p-6 md:col-span-3 md:row-span-2"
          >
            <div>
              <p className="eyebrow text-primary">Most used tool</p>
              <h3 className="mt-2 font-heading text-2xl font-bold text-foreground">
                Free VIN decoder — specs &amp; towing capacity by VIN
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Paste any 17-character VIN to get the exact build: engine, displacement, transmission, drivetrain, GVWR
                class, body class and plant — then jump straight to that vehicle's towing capacity, payload and MPG.
                Powered by NHTSA vPIC, no account needed.
              </p>
            </div>
            <div className="mt-8 rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-3 font-mono text-sm text-muted-foreground">
              1FTFW1E85MFA12345
              <span className="ml-2 text-primary group-hover:underline">→ Decode</span>
            </div>
          </Link>

          <Link
            to="/car-size-comparison"
            search={{ a: undefined, b: undefined }}
            className="card-premium group p-6 md:col-span-3"
          >
            <p className="eyebrow text-primary">Visual tool</p>
            <h3 className="mt-2 font-heading text-xl font-bold text-foreground transition-colors group-hover:text-primary">
              Car size comparison
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Overlay any two vehicles to scale and see real length, width, height and wheelbase differences before you
              commit to a garage or parking space.
            </p>
            <CardCta label="Compare two vehicles" />
          </Link>

          <Link to="/best" className="card-premium group p-6 md:col-span-3">
            <p className="eyebrow text-primary">Rankings</p>
            <h3 className="mt-2 font-heading text-xl font-bold text-foreground transition-colors group-hover:text-primary">
              Best gas mileage, ranked
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              The most efficient SUVs, trucks, hybrids and EVs, ordered by EPA-tested combined MPG, lowest annual fuel
              cost and lowest emissions.
            </p>
            <CardCta label="View rankings" />
          </Link>

          <Link to="/car-maintenance-schedule" className="card-premium group p-6 md:col-span-2">
            <p className="eyebrow text-primary">Ownership</p>
            <h3 className="mt-2 font-heading text-lg font-bold text-foreground transition-colors group-hover:text-primary">
              Maintenance schedule
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Mileage-by-mileage service intervals with PDF and calendar export.
            </p>
            <CardCta label="Open schedule" />
          </Link>

          <Link to="/compare" className="card-premium group p-6 md:col-span-2">
            <p className="eyebrow text-primary">Head-to-head</p>
            <h3 className="mt-2 font-heading text-lg font-bold text-foreground transition-colors group-hover:text-primary">
              Compare two vehicles
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              MPG, fuel cost, engine and drivetrain side by side, with a winner called.
            </p>
            <CardCta label="Start comparison" />
          </Link>

          <Link to="/tools" className="card-premium group p-6 md:col-span-2">
            <p className="eyebrow text-primary">Calculators</p>
            <h3 className="mt-2 font-heading text-lg font-bold text-foreground transition-colors group-hover:text-primary">
              Cost calculators
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Fuel cost, trip cost, payload, EV charging and gas-vs-EV over five years.
            </p>
            <CardCta label="See all 7 calculators" />
          </Link>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <AdSlot placement="home" />
      </div>

      {/* ---------- Browse by make ---------- */}
      <section className="border-y border-border bg-secondary/25">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <SectionHead
            eyebrow="Full database"
            title="Browse specifications by make"
            intro="Every make in the EPA database, with a dedicated page for each model, model year and spec type."
          />
          {makes.length === 0 ? (
            <div className="card-premium p-8 text-center">
              <p className="text-muted-foreground">Vehicle data is being loaded. Please check back shortly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {makes.map((make: HomeMake) => (
                <Link key={make.id} to="/$make" params={{ make: make.slug }} className="make-tile">
                  <BrandLogo makeSlug={make.slug} makeName={make.name} size={36} />
                  <span className="min-w-0 flex-1">
                    <span className="make-tile-name">{make.name}</span>
                    <span className="make-tile-sub">
                      {make.model_count} {make.model_count === 1 ? "model" : "models"}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---------- What you'll find ---------- */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <SectionHead
          eyebrow="Data coverage"
          title="Every spec type, on one page per vehicle"
          intro="Most sites split specs across separate domains. We keep MPG, engine, dimensions, safety and cost data together for each model."
        />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(
            [
              {
                title: "Fuel economy (MPG)",
                body: "City, highway and combined MPG for every model year, engine and trim, plus EV range and MPGe.",
                Icon: Fuel,
                cta: "See MPG rankings",
                href: "/best",
              },
              {
                title: "Engine & drivetrain",
                body: "Cylinders, displacement, forced induction, transmission type, gear count and drive layout.",
                Icon: Cog,
                cta: "Decode your engine",
                href: "/vin-decoder",
              },
              {
                title: "Dimensions & capacity",
                body: "Length, width, height, wheelbase, cargo volume and fuel tank capacity for size and fit decisions.",
                Icon: Ruler,
                cta: "Compare sizes visually",
                href: "/car-size-comparison",
                search: { a: undefined, b: undefined },
              },
              {
                title: "Towing & payload",
                body: "Towing capacity, payload and GVWR where published, with calculators for trailer weight.",
                Icon: Caravan,
                cta: "Open payload calculator",
                href: "/tools/$slug",
                params: { slug: "payload-calculator" },
              },
              {
                title: "Safety ratings",
                body: "NHTSA NCAP overall, frontal, side and rollover star ratings by model year.",
                Icon: ShieldCheck,
                cta: "Find your vehicle",
                href: "/search",
                search: { q: "", make: "", model: "", year: undefined, type: "", page: 1 },
              },
              {
                title: "Running costs",
                body: "Estimated annual fuel spend, CO₂ emissions and five-year gas-vs-EV cost of ownership.",
                Icon: PiggyBank,
                cta: "Run gas vs EV costs",
                href: "/tools/$slug",
                params: { slug: "gas-vs-ev-cost-calculator" },
              },
            ] as CoverageCard[]
          ).map((f) => (
            <Link
              key={f.title}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...({ to: f.href, params: f.params, search: f.search } as any)}
              className="card-premium group relative flex items-start gap-4 overflow-hidden p-5 sm:block sm:p-6"
            >
              <f.Icon className="icon-watermark h-24 w-24 sm:h-32 sm:w-32" strokeWidth={1.25} aria-hidden="true" />
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary sm:mb-4">
                <f.Icon className="h-5 w-5 text-primary" strokeWidth={2} aria-hidden="true" />
              </span>
              <span className="relative min-w-0 flex-1">
                <span className="block font-heading text-base font-semibold text-foreground transition-colors group-hover:text-primary sm:text-lg">
                  {f.title}
                </span>
                <span className="mt-1.5 block text-sm leading-relaxed text-muted-foreground">{f.body}</span>
                <CardCta label={f.cta} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ---------- Popular comparisons ---------- */}
      {/* ---------- Cross-brand spec charts ---------- */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <SectionHead
          eyebrow="Spec charts"
          title="Compare one spec across every brand"
          intro="Ranked tables that cut horizontally across the database — every model measured on the same number, sortable, with each row linking to that vehicle's full breakdown."
        />
        <ul className="flex flex-wrap gap-2">
          {TOPICS.map((t) => (
            <li key={t.slug}>
              <Link
                to={t.path}
                className="inline-block rounded-full border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary"
              >
                {t.h1}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/maintenance-comparison"
              className="inline-block rounded-full border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary"
            >
              Maintenance Intervals by Brand
            </Link>
          </li>
        </ul>
      </section>

      {comparisons.length > 0 ? (
        <section className="border-t border-border bg-secondary/25">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <SectionHead
              eyebrow="Head to head"
              title="Popular vehicle comparisons"
              intro="Real rivals only — each matchup pairs two mainstream models from the same EPA class, side by side on MPG, engines, fuel cost and drivetrain."
            />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(comparisons as ComparePairEntry[]).map((p) => (
                <div key={p.slug} className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-soft">
                  <span className="eyebrow text-primary">{p.cls}</span>
                  <div className="mt-3 flex items-center gap-3">
                    <BrandLogo makeSlug={p.aMakeSlug} makeName={p.a} size={32} />
                    <span className="min-w-0 flex-1 truncate font-heading text-sm font-semibold text-foreground">
                      {p.a}
                    </span>
                  </div>
                  <div className="my-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                    <span className="h-px flex-1 bg-border" aria-hidden="true" />
                    vs
                    <span className="h-px flex-1 bg-border" aria-hidden="true" />
                  </div>
                  <div className="flex items-center gap-3">
                    <BrandLogo makeSlug={p.bMakeSlug} makeName={p.b} size={32} />
                    <span className="min-w-0 flex-1 truncate font-heading text-sm font-semibold text-foreground">
                      {p.b}
                    </span>
                  </div>
                  <Link
                    to="/compare/$pair"
                    params={{ pair: p.slug }}
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl border border-primary px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                  >
                    Compare specs
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              ))}
            </div>
            <Link to="/compare" className="card-cta mt-6">
              See all comparisons
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </section>
      ) : null}

      {/* ---------- FAQ ---------- */}
      <section className="border-t border-border bg-secondary/25">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
          <SectionHead eyebrow="FAQ" title="Common questions" />
          <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
            {FAQS.map((f) => (
              <details key={f.q} className="group px-5 py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-base font-semibold text-foreground">
                  {f.q}
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="shrink-0 text-muted-foreground transition-transform group-open:rotate-45"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SEO prose ---------- */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <h2 className="text-xl font-bold text-foreground">About AutoSpecData</h2>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          AutoSpecData is a free vehicle specification database covering more than 15,800 car, truck and SUV
          configurations from 2015 to 2027. It aggregates fuel economy, engine and emissions data from the U.S.
          Department of Energy's fueleconomy.gov database with VIN decoding, dimensions and crash-test results from
          NHTSA. Unlike fragmented sites that split specs across multiple domains, we bring every data type — MPG,
          engine specs, dimensions, towing capacity, safety ratings and running costs — together in one place for each
          vehicle.
        </p>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          Every spec is sourced from official government data, not user submissions or estimates. When you see the
          verified badge, the figure comes directly from EPA testing or NHTSA records — the same sources dealerships and
          manufacturers reference. Pages are free to use, need no sign-up, and are supported by advertising.
        </p>
        <h3 className="mt-8 text-lg font-semibold text-foreground">What each spec type actually measures</h3>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          Fuel economy figures are EPA laboratory results: a combined rating weights the city cycle at 55% and the
          highway cycle at 45%, which is why the combined number is rarely the simple average of the two. Annual fuel
          cost assumes 15,000 miles a year at national average pump prices, so treat it as a way to rank vehicles
          against each other rather than a household budget. Electric and plug-in hybrid entries are additionally rated
          in MPGe, where 33.7 kWh is treated as the energy equivalent of one gallon of gasoline.
        </p>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          Dimensions and towing figures come from manufacturer submissions recorded in the NHTSA vPIC catalogue,
          reported per configuration — a crew cab with a short bed and a regular cab with a long bed are different rows
          because their wheelbase, length and rated capacity genuinely differ. Safety data combines NCAP crash-test star
          ratings, rollover resistance percentages, recall and complaint counts, and the availability of
          driver-assistance systems such as forward collision warning, lane departure warning and dynamic brake support.
          Maintenance intervals are manufacturer-published service schedules, normalised so brands can be compared on
          the same mileage basis.
        </p>
        <h3 className="mt-8 text-lg font-semibold text-foreground">How to research a vehicle here</h3>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          Start from the make directory above or the search page if you know the model. Each model hub links to its own
          MPG, engine, dimensions, towing, safety and maintenance pages, along with the rival models most often
          cross-shopped against it. If you are weighing two vehicles, the comparison pages put their figures side by
          side; if you are shopping by number rather than by badge, the ranking and chart pages sort the entire database
          by a single metric. For a car you already own or are inspecting, the VIN decoder returns the exact build
          details recorded with NHTSA, and the maintenance schedule tool turns a model's service intervals into a
          mileage plan you can export.
        </p>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          Data is refreshed as the EPA and NHTSA publish new model-year records, and every page states the source and
          the date its figures were last verified. Where a manufacturer has not reported a value, the field is left
          blank rather than estimated — an empty cell here means the government dataset has no figure, not that the
          vehicle lacks the feature.
        </p>
        <h3 className="mt-8 text-lg font-semibold text-foreground">Reading a spec sheet without being misled</h3>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          Most spec-sheet disputes come down to configuration. A maximum tow rating belongs to one exact build — a
          specific cab, bed length, axle ratio and drivetrain — and is quoted with a 150-pound driver aboard and nothing
          else in the vehicle. Add passengers, a tonneau, a toolbox and tongue weight at 10–15% of the trailer, and
          payload usually runs out thousands of pounds before the tow rating does. The number that governs your trailer
          is the payload figure on the door-jamb sticker of the truck in your driveway, not the headline in the
          brochure.
        </p>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          Dimensions carry the same trap. Published width excludes mirrors, which add roughly 8 to 12 inches, so a
          79-inch SUV needs about 89 inches of real clearance. Overall height is measured at curb weight without roof
          rails or a cargo box, and parking structures typically allow 80 inches. Wheelbase, not length, predicts how
          much of a vehicle is usable cabin: above 112 inches means adults fit in the second row, above 120 inches means
          a genuinely usable third row, and two vehicles of identical length can differ by eight inches of rear legroom
          depending on overhangs.
        </p>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          Fuel economy figures are comparative instruments. The highway cycle averages 48 mph, well below interstate
          speed, and drag rises with the square of velocity, so sustained 80 mph driving costs 15–20% against the
          sticker. Cold starts, winter fuel blends, roof loads and short trips widen the gap further. When you are
          converting a difference into money, work in gallons per 100 miles rather than MPG: moving from 20 to 25 MPG
          saves more fuel over a year than moving from 35 to 40.
        </p>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          Safety ratings are not interchangeable between model years. NHTSA revises its test protocol periodically, so a
          five-star result from an older program is not directly comparable with a five-star result under the current
          one, and an unrated configuration is simply untested rather than unsafe. Recall and complaint counts on a
          model page are raw volumes: a high-selling nameplate accumulates more of both without necessarily being less
          reliable, so read them against production volume and severity rather than as a score.
        </p>
      </section>
    </div>
  );
}
